import { useState, useEffect, useCallback } from 'react';
import { Search, ChevronRight, ArrowUp, ArrowDown, Minus, User, Shield, Brain, Briefcase, Users, MapPin, TrendingUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import apiClient from '@/lib/api';
import { toast } from 'sonner';

const LAYER_CONFIG = {
  behavior: { icon: Brain, label: 'Behavior', color: '#8b5cf6', desc: 'How someone behaves' },
  work: { icon: Briefcase, label: 'Work', color: '#2563eb', desc: 'What someone delivers' },
  relationship: { icon: Users, label: 'Relationship', color: '#c026d3', desc: 'How others experience them' },
  context: { icon: MapPin, label: 'Context', color: '#0891b2', desc: 'Where someone performs best' },
  growth: { icon: TrendingUp, label: 'Growth', color: '#16a34a', desc: 'Improving or declining' },
};

const ScoreBar = ({ value, max = 100, color }) => (
  <div className="flex items-center gap-2 flex-1">
    <div className="flex-1 h-1.5 bg-[#F0EDE8] overflow-hidden">
      <div className="h-full transition-all duration-500" style={{ width: `${(value / max) * 100}%`, backgroundColor: color }} />
    </div>
    <span className="text-[11px] font-mono font-bold ainaa-num" style={{ color }}>{value}</span>
  </div>
);

const DirectionBadge = ({ direction }) => {
  const map = {
    improving: { icon: ArrowUp, color: '#16a34a', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    declining: { icon: ArrowDown, color: '#dc2626', bg: 'bg-red-50', border: 'border-red-200' },
    steady: { icon: Minus, color: '#d97706', bg: 'bg-amber-50', border: 'border-amber-200' },
  };
  const cfg = map[direction] || map.steady;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 border ${cfg.bg} ${cfg.border}`} style={{ color: cfg.color }}>
      <Icon size={10} />{direction}
    </span>
  );
};

const Tag = ({ label, positive }) => (
  <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-1 mr-1 mb-1 border ${
    positive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'
  }`}>
    {label}
  </span>
);

/* ═══════════════════════════════
   OVERVIEW TABLE
   ═══════════════════════════════ */
const OverviewTable = ({ data, loading, onSelect }) => (
  <div className="border border-[#D8D3CB] bg-white overflow-x-auto">
    <table className="w-full" data-testid="introwspect-table">
      <thead>
        <tr className="border-b-2 border-[#D8D3CB]">
          <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589]">Member</th>
          <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589] hidden md:table-cell">Department</th>
          <th className="text-center px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589] hidden sm:table-cell">Trust</th>
          <th className="text-center px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589] hidden lg:table-cell">Behavior</th>
          <th className="text-center px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589] hidden lg:table-cell">Work</th>
          <th className="text-center px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589] hidden lg:table-cell">Relations</th>
          <th className="text-center px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589] hidden xl:table-cell">Growth</th>
          <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589] hidden md:table-cell">Tags</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={8} className="text-center py-16 text-[#9B9589]">Loading Introwspect data...</td></tr>
        ) : data.length === 0 ? (
          <tr><td colSpan={8} className="text-center py-16 text-[#9B9589]">No members found</td></tr>
        ) : data.map(m => (
          <tr key={m.ainaa_id} onClick={() => onSelect(m.ainaa_id)} className="border-b border-[#D8D3CB]/50 hover:bg-[#EDECEA]/30 cursor-pointer group" data-testid={`introwspect-row-${m.ainaa_id}`}>
            <td className="px-4 py-3">
              <span className="text-sm font-semibold text-[#0F1B2D] block">{m.name}</span>
              <span className="text-[10px] font-mono text-[#C4A35A]">{m.ainaa_id}</span>
            </td>
            <td className="px-4 py-3 hidden md:table-cell"><span className="text-xs text-[#4A4640]">{m.department || m.profession}</span></td>
            <td className="px-3 py-3 text-center hidden sm:table-cell"><span className="text-sm font-mono font-bold ainaa-num text-[#C4A35A]">{m.trust_score ?? '—'}</span></td>
            <td className="px-3 py-3 text-center hidden lg:table-cell"><span className="text-sm font-mono ainaa-num" style={{ color: '#8b5cf6' }}>{m.behavior_score}</span></td>
            <td className="px-3 py-3 text-center hidden lg:table-cell"><span className="text-sm font-mono ainaa-num" style={{ color: '#2563eb' }}>{m.work_score}</span></td>
            <td className="px-3 py-3 text-center hidden lg:table-cell"><span className="text-sm font-mono ainaa-num" style={{ color: '#c026d3' }}>{m.relationship_score}</span></td>
            <td className="px-3 py-3 text-center hidden xl:table-cell"><DirectionBadge direction={m.growth_direction} /></td>
            <td className="px-4 py-3 hidden md:table-cell">
              <div className="flex flex-wrap gap-0.5">
                {m.top_tags?.slice(0, 2).map(t => <Tag key={t} label={t} positive />)}
                {m.risk_tags?.slice(0, 1).map(t => <Tag key={t} label={t} />)}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ═══════════════════════════════
   MEMBER DETAIL VIEW
   ═══════════════════════════════ */
const MemberDetail = ({ ainaaId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient.get(`/admin/introwspect/${ainaaId}`)
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error('Failed to load Introwspect'))
      .finally(() => setLoading(false));
  }, [ainaaId]);

  if (loading) return <div className="text-center py-16 text-[#9B9589]">Loading Introwspect...</div>;
  if (!data) return <div className="text-center py-16 text-[#9B9589]">Not found</div>;

  const { member, trust_score, layers, tags, specter_insights } = data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={onBack} className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589] hover:text-[#0F1B2D] mb-2 block" data-testid="introwspect-back">Back to Overview</button>
          <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D]">{member.name}</h3>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-[11px] font-mono font-bold text-[#C4A35A]">{member.ainaa_id}</span>
            <span className="text-xs text-[#9B9589]">{member.profession}</span>
            <span className="text-xs text-[#9B9589]">{member.department}</span>
          </div>
        </div>
        <div className="text-right">
          <span className="text-[9px] font-bold uppercase tracking-wider text-[#9B9589] block">Trust Score</span>
          <span className="text-3xl font-mono font-bold text-[#C4A35A] ainaa-num">{trust_score ?? '—'}</span>
        </div>
      </div>

      {/* Specter Insights */}
      <div className="border-2 border-[#0F1B2D] bg-[#0F1B2D] p-5 mb-6" data-testid="specter-insights">
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#C4A35A] mb-3">Specter Insights</h4>
        <div className="space-y-2">
          {specter_insights.map((insight, i) => (
            <p key={i} className="text-sm text-[#E8E4DD] leading-relaxed">
              <span className="text-[#C4A35A] mr-1.5 font-bold">&mdash;</span>{insight}
            </p>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div className="flex gap-6 mb-6" data-testid="introwspect-tags">
        <div className="flex-1">
          <span className="text-[9px] font-bold uppercase tracking-wider text-[#16a34a] block mb-2">Positive Tags</span>
          <div className="flex flex-wrap">{tags.positive.map(t => <Tag key={t} label={t} positive />)}</div>
        </div>
        <div className="flex-1">
          <span className="text-[9px] font-bold uppercase tracking-wider text-[#dc2626] block mb-2">Risk Tags</span>
          <div className="flex flex-wrap">{tags.negative.map(t => <Tag key={t} label={t} />)}</div>
        </div>
      </div>

      {/* 5 Layers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(layers).map(([key, layer]) => (
          <LayerCard key={key} layerKey={key} layer={layer} />
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════
   LAYER CARD
   ═══════════════════════════════ */
const LayerCard = ({ layerKey, layer }) => {
  const cfg = LAYER_CONFIG[layerKey];
  const Icon = cfg.icon;

  return (
    <div className="border border-[#D8D3CB] bg-white p-4" data-testid={`layer-${layerKey}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={14} style={{ color: cfg.color }} />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: cfg.color }}>{cfg.label} Layer</span>
        </div>
        <span className="text-lg font-mono font-bold ainaa-num" style={{ color: cfg.color }}>{layer.score}</span>
      </div>

      {/* Description */}
      <p className="text-[10px] text-[#9B9589] mb-3">{cfg.desc}</p>

      {/* Labels */}
      <div className="flex flex-wrap gap-1 mb-3">
        {layer.labels.map(l => (
          <span key={l} className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 border border-[#D8D3CB] bg-[#F8F7F5] text-[#4A4640]">{l}</span>
        ))}
      </div>

      {/* Signals */}
      <div className="space-y-1.5">
        {Object.entries(layer.signals).map(([key, val]) => {
          if (key === 'score_trend') return null;
          if (Array.isArray(val)) {
            return (
              <div key={key} className="flex items-center gap-2">
                <span className="text-[9px] text-[#9B9589] w-[140px] flex-shrink-0">{key.replace(/_/g, ' ')}</span>
                <span className="text-[10px] text-[#4A4640]">{val.join(', ')}</span>
              </div>
            );
          }
          if (typeof val === 'number' && val <= 100 && val >= 0 && !key.includes('count') && !key.includes('incidents') && !key.includes('reports') && !key.includes('roles') && !key.includes('tasks') && !key.includes('avg') && !key.includes('frequency') && !key.includes('reduction') && !key.includes('increase') && !key.includes('improvement')) {
            return (
              <div key={key} className="flex items-center gap-2">
                <span className="text-[9px] text-[#9B9589] w-[140px] flex-shrink-0">{key.replace(/_/g, ' ')}</span>
                <ScoreBar value={val} color={cfg.color} />
              </div>
            );
          }
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="text-[9px] text-[#9B9589] w-[140px] flex-shrink-0">{key.replace(/_/g, ' ')}</span>
              <span className="text-[11px] font-mono text-[#0F1B2D] ainaa-num">{typeof val === 'number' ? val : String(val)}</span>
              {typeof val === 'number' && key.includes('reduction') && val > 0 && <ArrowDown size={10} className="text-emerald-500" />}
              {typeof val === 'number' && key.includes('increase') && val > 0 && <ArrowUp size={10} className="text-emerald-500" />}
            </div>
          );
        })}
      </div>

      {/* Growth trend mini chart */}
      {layer.signals.score_trend && (
        <div className="mt-3 pt-3 border-t border-[#F0EDE8]">
          <span className="text-[9px] text-[#9B9589] block mb-2">Score Trend (12 months)</span>
          <div className="flex items-end gap-0.5 h-12">
            {layer.signals.score_trend.map((pt, i) => (
              <div key={i} className="flex-1 transition-all" style={{ height: `${pt.score}%`, backgroundColor: cfg.color, opacity: 0.4 + (i / layer.signals.score_trend.length) * 0.6 }} title={`Month ${pt.month}: ${pt.score}`} />
            ))}
          </div>
          {layer.direction && (
            <div className="mt-2"><DirectionBadge direction={layer.direction} /></div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════
   MAIN PANEL
   ═══════════════════════════════ */
const IntrowspectPanel = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${search}&limit=20` : '?limit=20';
      const { data: res } = await apiClient.get(`/admin/introwspect${params}`);
      setData(res.items || []);
      setTotal(res.total || 0);
    } catch { toast.error('Failed to load Introwspect'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  if (selectedId) {
    return <MemberDetail ainaaId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#0F1B2D] mb-1">Introwspect</h3>
        <p className="text-xs text-[#9B9589]">The behavioral engine behind Trust Score. Observes, interprets, predicts, and continuously updates professional perception.</p>
      </div>

      {/* Search */}
      <div className="flex gap-3 mb-5 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9589]" size={16} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search members by name, ID, or department..." className="input-ainaa pl-11 h-10" data-testid="introwspect-search" />
        </div>
        <span className="text-xs text-[#9B9589] font-medium ainaa-num">{total} members</span>
      </div>

      <OverviewTable data={data} loading={loading} onSelect={setSelectedId} />
    </div>
  );
};

export default IntrowspectPanel;
